import { useEffect, useMemo, useState, useCallback } from "react";
import { Megaphone, PartyPopper, HeartHandshake, Info, Zap, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import api, { formatDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const CAT_META = {
  general:    { label: "Umum",       icon: Info,           bg: "bg-blue-100",    icon_cls: "text-blue-700",    accent: "bg-blue-500",    pill: "bg-blue-100 text-blue-800" },
  holiday:    { label: "Hari Libur", icon: PartyPopper,    bg: "bg-emerald-100", icon_cls: "text-emerald-700", accent: "bg-emerald-500", pill: "bg-emerald-100 text-emerald-800" },
  condolence: { label: "Duka Cita",  icon: HeartHandshake, bg: "bg-slate-200",   icon_cls: "text-slate-700",   accent: "bg-slate-500",   pill: "bg-slate-200 text-slate-800" },
  urgent:     { label: "Mendesak",   icon: Zap,            bg: "bg-rose-100",    icon_cls: "text-rose-700",    accent: "bg-rose-500",    pill: "bg-rose-100 text-rose-800" },
};

// Global forced-acknowledgment popup for active announcements the current user hasn't
// yet ticked "Informasi Sudah Saya Terima". Mounted once in AppShell. Refetches every
// 60 seconds so newly-published announcements popup even without a page refresh.
export default function AnnouncePopup() {
  const { user } = useAuth();
  const [unread, setUnread] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/announcements/active");
      const pending = (data || []).filter((a) => !a.acknowledged);
      setUnread(pending);
    } catch {
      // silently ignore — popup is non-critical
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user, load]);

  const current = unread[0];
  const total = unread.length;

  const ack = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api.post(`/announcements/${current.id}/acknowledge`);
      setUnread((xs) => xs.slice(1));
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const meta = useMemo(() => (current ? CAT_META[current.category] || CAT_META.general : null), [current]);
  if (!current || !meta) return null;
  const Icon = meta.icon;

  return (
    <Dialog open={true}>
      <DialogContent
        className="rounded-none max-w-2xl p-0 overflow-hidden [&>button]:hidden"
        data-testid="announce-popup"
      >
        {/* Coloured banner top */}
        <div className={`px-6 py-4 ${meta.accent} text-white flex items-center gap-3`}>
          <div className="w-11 h-11 bg-white/20 flex items-center justify-center backdrop-blur-sm shrink-0">
            <Icon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest font-semibold opacity-90">Pengumuman · {meta.label}</p>
            <h2 className="font-display text-lg tracking-tight text-white truncate" data-testid="popup-title">{current.title}</h2>
          </div>
          {total > 1 && (
            <span className="text-[10px] uppercase tracking-wider font-bold bg-white/20 px-2 py-1" data-testid="popup-queue-count">
              1 / {total}
            </span>
          )}
        </div>

        <DialogHeader className="sr-only">
          <DialogTitle>{current.title}</DialogTitle>
          <DialogDescription>Pengumuman yang wajib dikonfirmasi oleh penerima.</DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-4 max-h-[55vh] overflow-y-auto">
          <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="popup-body">{current.body}</p>
          <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-1 pt-3 border-t border-slate-200">
            <span className="inline-flex items-center gap-1">
              <Calendar size={12} />
              Berlaku: {fmtDate(current.start_date)} – {fmtDate(current.end_date)}
            </span>
            <span className="italic">
              · dari {current.author_name} ({current.author_division})
            </span>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <p className="text-[11px] text-slate-500 italic order-2 sm:order-1 flex-1 min-w-0">
            Anda dapat membaca pengumuman ini kembali di Dashboard atau menu <span className="font-semibold not-italic">Announcements</span>.
          </p>
          <Button
            type="button"
            data-testid="popup-ack"
            onClick={ack}
            disabled={busy}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold order-1 sm:order-2 whitespace-nowrap px-6 py-2.5 h-auto shrink-0"
          >
            <CheckCircle2 size={15} className="mr-2" />
            {busy ? "Menyimpan…" : "Informasi Sudah Saya Terima"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
